import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const NAV_LINKS = [
  { to: '/features', label: 'Features' },
  { to: '/how-it-works', label: 'How It Works' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/faq', label: 'FAQ' },
];

const FAQ_CATEGORIES = [
  {
    category: 'Getting Started',
    icon: '🚀',
    faqs: [
      { q: 'How do I get started with Prorately?', a: 'Sign up for a free 14-day trial — no credit card needed. Once you\'re in, install the Chrome Extension (takes 30 seconds), import your client book via CSV or CRM sync, and you\'re ready to autofill your first quote. Most agents are fully operational within 10 minutes.' },
      { q: 'Do I need to be technical to use Prorately?', a: 'Not at all. If you can use Chrome and a spreadsheet, you can use Prorately. There\'s no coding, no IT setup, and no onboarding call required. Our setup wizard walks you through every step.' },
      { q: 'Is there an onboarding call or training?', a: 'Pro and Agency plan users get access to a live onboarding session with our team. Solo users get access to our full video library and help docs. Most agents find they don\'t need it — the product is designed to be self-explanatory.' },
      { q: 'Can I try Prorately before committing to a plan?', a: 'Yes — every plan includes a full-featured 14-day free trial. You get access to everything on your chosen tier, including the Chrome Extension, CRM, and all integrations. No credit card is required to start.' },
    ],
  },
  {
    category: 'The Chrome Extension',
    icon: '🧩',
    faqs: [
      { q: 'Which browsers does the extension support?', a: 'The Prorately extension works on Chrome, Brave, Edge, and any other Chromium-based browser. A Firefox version is available in beta — contact support to request access.' },
      { q: 'Which carrier portals are supported?', a: 'We support 40+ portals including EZLynx, Applied Epic, Hawksoft, QQ Catalyst, and all major direct carrier portals including Progressive, Travelers, State Farm, Nationwide, and more. We add new portals weekly — check our integrations page for the full list.' },
      { q: 'What happens if a carrier updates their form layout?', a: 'Our engineering team monitors all supported portals continuously. When a carrier changes their form structure, we push an extension update — typically within 24 hours. You\'ll see an automatic update notification in Chrome.' },
      { q: 'Does the extension slow down my browser?', a: 'No measurable impact. The extension sits dormant until it detects a supported quoting form, then activates. It uses minimal memory and doesn\'t run background processes on unrelated tabs.' },
      { q: 'Can I use the extension on multiple computers?', a: 'Yes. Sign into the Prorately extension on any computer and all your client data syncs instantly. There\'s no limit on the number of devices per account.' },
    ],
  },
  {
    category: 'Client Data & CRM',
    icon: '🧠',
    faqs: [
      { q: 'How do I import my existing clients?', a: 'Upload a CSV export from your current CRM or spreadsheet, and Prorately\'s import wizard maps your columns automatically. You can also connect directly via native integrations with AgencyZoom, Salesforce, HubSpot, and Applied Epic.' },
      { q: 'What client data can I store?', a: 'Everything. Prorately supports unlimited drivers, vehicles, properties, prior coverage history, policy numbers, notes, and custom fields. You can store any data point relevant to your quoting workflow.' },
      { q: 'Can multiple agents share the same client records?', a: 'Yes — on Pro plans, up to 3 agents can collaborate on shared client records. Agency plans support unlimited agents with manager-level permissions, ownership assignment, and a full activity log.' },
      { q: 'What happens to my data if I cancel?', a: 'Your account moves to read-only mode — no data is deleted. You have 90 days to export everything. We\'ll send you a reminder at 30 days and again at 60 days before any data is removed.' },
    ],
  },
  {
    category: 'Security & Compliance',
    icon: '🔒',
    faqs: [
      { q: 'Is my client data secure?', a: 'Yes. All data is encrypted in transit (TLS 1.3) and at rest (AES-256). We\'re SOC 2 Type II certified, meaning an independent auditor has verified our security controls annually. We never sell or share your data with third parties.' },
      { q: 'Is Prorately HIPAA compliant?', a: 'Yes — Prorately is fully HIPAA compliant for health insurance workflows. Agency plan users can request a signed Business Associate Agreement (BAA) from our compliance team.' },
      { q: 'Where is my data stored?', a: 'All data is stored in SOC 2-certified data centers in the United States. We do not store client data outside the US. Backups run every 6 hours with 30-day retention.' },
      { q: 'Can I get compliance documentation for my agency?', a: 'Yes — Agency plan users get access to our full compliance package, including our SOC 2 report, HIPAA compliance docs, data processing agreement, and security overview. Contact your account manager to request these.' },
    ],
  },
  {
    category: 'Billing & Plans',
    icon: '💳',
    faqs: [
      { q: 'Can I switch plans at any time?', a: 'Yes. Upgrade instantly from your account settings — the change takes effect immediately and is prorated. Downgrades take effect at the start of your next billing cycle.' },
      { q: 'Do you offer annual billing discounts?', a: 'Yes — annual billing saves you the equivalent of 2 months compared to monthly billing. You can toggle between monthly and annual on our pricing page to see the exact savings.' },
      { q: 'What payment methods do you accept?', a: 'We accept all major credit and debit cards (Visa, Mastercard, Amex, Discover). Agency plan customers can also pay by ACH bank transfer or request an annual invoice.' },
      { q: 'Is there a setup fee or long-term contract?', a: 'No setup fees, no contracts, ever. Monthly plans are truly month-to-month. Annual plans are prepaid for 12 months but come with a 30-day money-back guarantee.' },
      { q: 'Do you offer discounts for non-profits or small agencies?', a: 'Yes — contact our sales team for non-profit pricing. We also offer volume discounts for agencies with 10+ agents on the Agency plan.' },
    ],
  },
];

function SharedNav({ mobileOpen, setMobileOpen }) {
  return (
    <>
      <style>{`
        .nav{position:sticky;top:0;z-index:100;background:rgba(255,255,255,0.92);backdrop-filter:blur(16px);border-bottom:1px solid #E2E8F0;padding:0 2rem;height:68px;display:flex;align-items:center;justify-content:space-between;}
        .nav-logo{font-family:'Sora',sans-serif;font-size:1.25rem;font-weight:800;color:#2563EB;letter-spacing:-0.5px;text-decoration:none;}
        .nav-links a{color:#475569;font-size:0.9rem;font-weight:500;text-decoration:none;margin-left:2rem;transition:color 0.2s;}
        .nav-links a:hover{color:#2563EB;}
        .btn-nav{background:#2563EB;color:#fff;padding:0.55rem 1.4rem;border-radius:8px;font-size:0.9rem;font-weight:600;text-decoration:none;transition:background 0.2s,transform 0.15s;border:none;cursor:pointer;}
        .btn-nav:hover{background:#1D4ED8;transform:translateY(-1px);}
        .hamburger{display:none;background:none;border:1px solid #E2E8F0;border-radius:8px;padding:0.4rem 0.6rem;cursor:pointer;font-size:1.2rem;}
        @media(max-width:768px){
          .nav-links,.nav-desktop-cta{display:none !important;}
          .hamburger{display:block;}
          .mobile-menu{position:fixed;top:68px;left:0;right:0;background:#fff;border-bottom:1px solid #E2E8F0;padding:1.5rem 2rem;z-index:99;box-shadow:0 8px 32px rgba(0,0,0,0.08);}
          .mobile-menu a{display:block;padding:0.75rem 0;color:#0F172A;font-size:1rem;font-weight:500;text-decoration:none;border-bottom:1px solid #f1f5f9;}
          .mobile-menu a:last-child{border-bottom:none;}
        }
      `}</style>
      <nav className="nav">
        <Link to="/" className="nav-logo">⚡ Prorately</Link>
        <div className="nav-links" style={{ display: 'flex', alignItems: 'center' }}>
          {NAV_LINKS.map(l => <Link key={l.to} to={l.to}>{l.label}</Link>)}
        </div>
        <div className="nav-desktop-cta" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Link to="/login" style={{ color: '#475569', fontWeight: 500, textDecoration: 'none', fontSize: '0.9rem' }}>Login</Link>
          <Link to="/register" className="btn-nav">Start Free Trial</Link>
        </div>
        <button className="hamburger" onClick={() => setMobileOpen(!mobileOpen)}>☰</button>
      </nav>
      {mobileOpen && (
        <div className="mobile-menu">
          {NAV_LINKS.map(l => <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)}>{l.label}</Link>)}
          <Link to="/login" onClick={() => setMobileOpen(false)}>Login</Link>
          <Link to="/register" onClick={() => setMobileOpen(false)} style={{ color: '#2563EB', fontWeight: 700 }}>Start Free Trial →</Link>
        </div>
      )}
    </>
  );
}

export default function FAQ() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openItem, setOpenItem] = useState(null);
  const [activeCategory, setActiveCategory] = useState(0);
  const [search, setSearch] = useState('');

  const toggle = (key) => setOpenItem(openItem === key ? null : key);

  const filteredCategories = search.trim().length > 1
    ? FAQ_CATEGORIES.map(cat => ({
        ...cat,
        faqs: cat.faqs.filter(f =>
          f.q.toLowerCase().includes(search.toLowerCase()) ||
          f.a.toLowerCase().includes(search.toLowerCase())
        ),
      })).filter(cat => cat.faqs.length > 0)
    : FAQ_CATEGORIES;

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", background: '#F8FAFF', color: '#0F172A', overflowX: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Sora:wght@400;600;700;800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        html{scroll-behavior:smooth;}
        .sora{font-family:'Sora',sans-serif;}

        .page-hero{background:linear-gradient(135deg,#1E3A8A 0%,#2563EB 60%,#3B82F6 100%);padding:5rem 2rem 4rem;text-align:center;position:relative;overflow:hidden;}
        .page-hero::before{content:'';position:absolute;inset:0;background-image:linear-gradient(rgba(255,255,255,0.05) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.05) 1px,transparent 1px);background-size:40px 40px;}
        .page-hero-content{position:relative;z-index:1;max-width:680px;margin:0 auto;}
        .page-tag{display:inline-block;background:rgba(255,255,255,0.15);border:1px solid rgba(255,255,255,0.3);color:#fff;font-size:0.75rem;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;padding:0.35rem 0.9rem;border-radius:100px;margin-bottom:1.25rem;}
        .page-hero h1{font-family:'Sora',sans-serif;font-size:clamp(2rem,4vw,3rem);font-weight:800;color:#fff;line-height:1.15;letter-spacing:-0.03em;margin-bottom:1rem;}
        .page-hero p{color:rgba(255,255,255,0.8);font-size:1.05rem;line-height:1.7;margin-bottom:2rem;}

        /* Search */
        .search-wrap{position:relative;max-width:520px;margin:0 auto;}
        .search-icon{position:absolute;left:1rem;top:50%;transform:translateY(-50%);font-size:1rem;color:rgba(255,255,255,0.6);}
        .search-input{width:100%;padding:0.9rem 1rem 0.9rem 2.8rem;border-radius:12px;border:1.5px solid rgba(255,255,255,0.3);background:rgba(255,255,255,0.15);color:#fff;font-size:0.95rem;font-family:'DM Sans',sans-serif;outline:none;transition:all 0.2s;backdrop-filter:blur(8px);}
        .search-input::placeholder{color:rgba(255,255,255,0.55);}
        .search-input:focus{background:rgba(255,255,255,0.22);border-color:rgba(255,255,255,0.55);}

        /* Layout */
        .faq-layout{max-width:1060px;margin:0 auto;padding:4rem 2rem;display:grid;grid-template-columns:220px 1fr;gap:3rem;align-items:start;}
        @media(max-width:768px){.faq-layout{grid-template-columns:1fr;gap:2rem;}.cat-sidebar{display:none;}}

        /* Sidebar */
        .cat-sidebar{position:sticky;top:88px;}
        .cat-btn{display:flex;align-items:center;gap:0.65rem;width:100%;text-align:left;background:none;border:none;cursor:pointer;padding:0.65rem 0.9rem;border-radius:10px;font-size:0.88rem;font-weight:500;color:#64748B;transition:all 0.2s;margin-bottom:0.25rem;}
        .cat-btn:hover{background:#EFF6FF;color:#2563EB;}
        .cat-btn.active{background:#EFF6FF;color:#2563EB;font-weight:700;}
        .cat-btn-icon{font-size:1rem;width:22px;text-align:center;}

        /* FAQ content */
        .faq-content{}
        .faq-category-block{margin-bottom:3rem;}
        .faq-cat-header{display:flex;align-items:center;gap:0.75rem;margin-bottom:1.5rem;}
        .faq-cat-icon{width:40px;height:40px;background:#EFF6FF;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:1.1rem;flex-shrink:0;}
        .faq-cat-name{font-family:'Sora',sans-serif;font-weight:800;font-size:1.1rem;color:#0F172A;}
        .faq-cat-count{font-size:0.78rem;color:#94A3B8;margin-top:0.1rem;}

        .faq-item{border:1.5px solid #E2E8F0;border-radius:12px;margin-bottom:0.75rem;background:#fff;transition:border-color 0.2s;}
        .faq-item.open{border-color:#BFDBFE;}
        .faq-question{display:flex;justify-content:space-between;align-items:center;cursor:pointer;gap:1rem;padding:1.15rem 1.25rem;}
        .faq-q-text{font-weight:600;color:#0F172A;font-size:0.95rem;line-height:1.4;}
        .faq-chevron{color:#94A3B8;font-size:1rem;transition:transform 0.25s;flex-shrink:0;width:20px;height:20px;display:flex;align-items:center;justify-content:center;border-radius:50%;background:#F1F5F9;}
        .faq-chevron.open{transform:rotate(180deg);color:#2563EB;background:#EFF6FF;}
        .faq-answer{color:#64748B;font-size:0.92rem;line-height:1.75;max-height:0;overflow:hidden;transition:max-height 0.35s ease,padding 0.25s;padding:0 1.25rem;}
        .faq-answer.open{max-height:300px;padding:0 1.25rem 1.25rem;}

        /* No results */
        .no-results{text-align:center;padding:4rem 2rem;color:#94A3B8;}
        .no-results-icon{font-size:2.5rem;margin-bottom:1rem;}
        .no-results h3{font-family:'Sora',sans-serif;font-size:1.1rem;color:#475569;margin-bottom:0.5rem;}

        /* Contact CTA */
        .contact-cta{background:#fff;border:1.5px solid #E2E8F0;border-radius:20px;padding:2.5rem;text-align:center;margin:0 2rem 3rem;max-width:800px;margin-left:auto;margin-right:auto;}
        .contact-cta h3{font-family:'Sora',sans-serif;font-weight:800;font-size:1.2rem;color:#0F172A;margin-bottom:0.5rem;}
        .contact-cta p{color:#64748B;font-size:0.92rem;margin-bottom:1.5rem;line-height:1.6;}
        .contact-btns{display:flex;gap:1rem;justify-content:center;flex-wrap:wrap;}
        .btn-outline{padding:0.75rem 1.5rem;border-radius:10px;font-weight:600;font-size:0.9rem;text-decoration:none;border:1.5px solid #E2E8F0;color:#475569;transition:all 0.2s;cursor:pointer;background:#fff;}
        .btn-outline:hover{border-color:#2563EB;color:#2563EB;}
        .btn-primary-sm{padding:0.75rem 1.5rem;border-radius:10px;font-weight:700;font-size:0.9rem;text-decoration:none;background:#2563EB;color:#fff;transition:all 0.2s;border:none;cursor:pointer;}
        .btn-primary-sm:hover{background:#1D4ED8;transform:translateY(-1px);}

        /* Page CTA */
        .page-cta{background:#0F172A;padding:5rem 2rem;text-align:center;}
        .page-cta h2{font-family:'Sora',sans-serif;font-size:clamp(1.8rem,3.5vw,2.5rem);font-weight:800;color:#fff;margin-bottom:1rem;letter-spacing:-0.02em;}
        .page-cta p{color:#64748B;font-size:1rem;margin-bottom:2rem;max-width:480px;margin-left:auto;margin-right:auto;line-height:1.7;}
        .btn-blue{background:#2563EB;color:#fff;padding:0.9rem 2rem;border-radius:12px;font-weight:700;font-size:1rem;text-decoration:none;border:none;cursor:pointer;transition:all 0.2s;display:inline-flex;align-items:center;gap:0.5rem;box-shadow:0 4px 16px rgba(37,99,235,0.3);}
        .btn-blue:hover{background:#1D4ED8;transform:translateY(-1px);}

        footer{background:#0F172A;color:#94A3B8;padding:3rem 2rem 2rem;border-top:1px solid #1E293B;}
        .footer-bottom-simple{max-width:1100px;margin:0 auto;display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:1rem;font-size:0.82rem;}
        .footer-bottom-simple a{color:#64748B;text-decoration:none;margin-left:1.5rem;}
        .footer-bottom-simple a:hover{color:#93C5FD;}
      `}</style>

      <SharedNav mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      <div className="page-hero">
        <div className="page-hero-content">
          <span className="page-tag">FAQ</span>
          <h1 className="sora">Questions? We have answers.</h1>
          <p>Everything you need to know about Prorately — from setup to security to billing.</p>
          <div className="search-wrap">
            <span className="search-icon">🔍</span>
            <input
              className="search-input"
              type="text"
              placeholder="Search questions…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="faq-layout">
        {/* Sidebar */}
        <div className="cat-sidebar">
          {FAQ_CATEGORIES.map((cat, i) => (
            <button
              key={cat.category}
              className={`cat-btn${activeCategory === i ? ' active' : ''}`}
              onClick={() => setActiveCategory(i)}
            >
              <span className="cat-btn-icon">{cat.icon}</span>
              {cat.category}
            </button>
          ))}
        </div>

        {/* FAQ list */}
        <div className="faq-content">
          {filteredCategories.length === 0 ? (
            <div className="no-results">
              <div className="no-results-icon">🤷</div>
              <h3>No results for "{search}"</h3>
              <p>Try a different search term or browse the categories.</p>
            </div>
          ) : (
            filteredCategories.map((cat, ci) => (
              <div className="faq-category-block" key={cat.category}>
                <div className="faq-cat-header">
                  <div className="faq-cat-icon">{cat.icon}</div>
                  <div>
                    <div className="faq-cat-name">{cat.category}</div>
                    <div className="faq-cat-count">{cat.faqs.length} questions</div>
                  </div>
                </div>
                {cat.faqs.map((faq, fi) => {
                  const key = `${ci}-${fi}`;
                  const isOpen = openItem === key;
                  return (
                    <div className={`faq-item${isOpen ? ' open' : ''}`} key={fi}>
                      <div className="faq-question" onClick={() => toggle(key)}>
                        <span className="faq-q-text">{faq.q}</span>
                        <span className={`faq-chevron${isOpen ? ' open' : ''}`}>▾</span>
                      </div>
                      <div className={`faq-answer${isOpen ? ' open' : ''}`}>{faq.a}</div>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Contact */}
      <div className="contact-cta">
        <h3>Still have questions?</h3>
        <p>Our support team is available Monday–Friday, 8am–6pm CT. Most questions get a reply within 2 hours.</p>
        <div className="contact-btns">
          <a href="mailto:support@prorately.com" className="btn-outline">✉ Email Support</a>
          <button className="btn-outline">💬 Live Chat</button>
          <Link to="/register" className="btn-primary-sm">Start Free Trial →</Link>
        </div>
      </div>

      <div className="page-cta">
        <h2 className="sora">Ready to stop copy-pasting?</h2>
        <p>14-day free trial. No credit card. No setup calls. Just install and start quoting faster.</p>
        <Link to="/register" className="btn-blue">Start Free Trial →</Link>
      </div>

      <footer>
        <div className="footer-bottom-simple">
          <span>© {new Date().getFullYear()} Prorately. All rights reserved.</span>
          <div>
            <a href="#">Privacy</a>
            <a href="#">Terms</a>
            <a href="#">Security</a>
          </div>
        </div>
      </footer>
    </div>
  );
}